import { OrgConsentEvent } from '@experiences/telemetry';
import { GlobalStyles } from '@experiences/theme';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApToggleSwitch,
    ApTooltip,
} from '@uipath/portal-shell-react';
import cx from 'clsx';
import React from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import { Link } from 'react-router';

import { type Consent } from '../../services/identity/ConsentService';
import { useTelemetryHelper } from '../../telemetry/TelemetryHelper';
import { useConsentList } from './ConsentListViewModel';

const useStyles = makeStyles(theme => ({
    ...GlobalStyles(theme),
    cardFixedWidth: { width: '320px' },
    cardContainer: {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme.palette.semantic.colorBorderDeEmp,
        borderRadius: '8px',
    },
    truncate: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}));

interface ConsentCardProps {
    consent: Consent;
    isCardView?: boolean;
}

export const ConsentCard = ({
    consent, isCardView = false,
}: ConsentCardProps) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const { logEvent } = useTelemetryHelper();

    const {
        onEdit, onDelete, onActivateDeactivate,
    } = useConsentList();

    return (
        <UiStack
            gap={SpacingToken.S}
            p={SpacingToken.S}
            direction="column"
            className={cx(classes.cardContainer, !isCardView && classes.cardFixedWidth)}>
            <UiStack
                direction="column"
                className={classes.truncate}>
                <ApTooltip title={consent.content?.title}>
                    <UiText
                        variant={FontVariantToken.fontSizeLBold}
                        className={classes.truncate}>
                        {consent.content?.title}
                    </UiText>
                </ApTooltip>
                <UiText variant={FontVariantToken.fontSizeXsBold}>
                    {`${translate({ id: 'CLIENT_DATE_CREATED' })}: ${new Date(consent.creationTime).toLocaleDateString()}`}
                </UiText>
            </UiStack>

            <UiStack direction="column">
                <UiText variant={FontVariantToken.fontSizeS}>
                    <FormattedMessage
                        id="CLIENT_USER_CONSENT_USER_RECORD_COUNT"
                        values={{
                            count: consent.userRecordCount ?? 0,
                            link: consent.userRecordCount && consent.userRecordCount > 0 ?
                                <UiText
                                    display="inline"
                                    variant={FontVariantToken.fontSizeSBold}>
                                    <Link
                                        className={classes.a}
                                        data-cy="consent-view-user-records"
                                        data-testid="consent-view-user-records"
                                        onClick={() => logEvent(OrgConsentEvent.ViewUserRecords, { consentId: consent.id })}
                                        to={`?consentId=${consent.id}`}>
                                        {translate({ id: 'CLIENT_USER_CONSENT_SEE_USER_LIST' })}
                                    </Link>
                                </UiText> : '',
                        }}
                    />
                </UiText>
            </UiStack>

            <UiStack
                justify="between"
                align="center">
                <ApToggleSwitch
                    style={{ height: '20px' }}
                    checked={consent.isActive}
                    onValueChanged={(e) => onActivateDeactivate(e.detail, consent)}
                    data-cy="consent-status-switch"
                    data-testid="consent-status-switch"
                    label={translate({ id: consent.isActive ? 'CLIENT_ACTIVE' : 'CLIENT_INACTIVE' })}
                />
                <UiStack>
                    <IconButton
                        onClick={() => onEdit(consent.id)}
                        data-cy="consent-edit-button"
                        data-testid="consent-edit-button">
                        <EditOutlinedIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => onDelete(consent.id)}
                        data-cy="consent-delete-button"
                        data-testid="consent-delete-button">
                        <DeleteOutlinedIcon />
                    </IconButton>
                </UiStack>
            </UiStack>
        </UiStack>
    );
};
